/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { HiOutlineCreditCard } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import { Card } from "../../ui";
import type { Column } from "../../ui";
import { _PAGE_SIZE_ } from "../../lib/other/tools";
import { usePaymentContext } from "../../context/PaymentContext";

type PaymentRow = {
  id?: number | string;
  tx_ref?: string;
  content_type?: string;
  content_name?: string;
  name?: string;
  start_date?: string;
  start_time?: string;
  amount?: number | string;
  currency?: string;
  card_type?: string;
  card_last_4digits?: string;
  card_expiry_date?: string;
  status_message?: string;
  createdAt?: string;
  [key: string]: unknown;
};

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  if (status === "successful") {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
        Successful
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
        Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-inset ring-red-200">
      {status || "failed"}
    </span>
  );
};

const Overview: React.FC = () => {
  const { payment, fetchPayments, count, hasCompany } =
    usePaymentContext() as any;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!payment) fetchPayments(page, pageSize);
  }, [payment]);

  const rows = useMemo<PaymentRow[] | null>(() => {
    if (!Array.isArray(payment)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return payment as PaymentRow[];
    return (payment as PaymentRow[]).filter((p) =>
      [p.name, p.content_name, p.tx_ref].some((v) =>
        String(v ?? "").toLowerCase().includes(q),
      ),
    );
  }, [payment, query]);

  const totalsByCurrency = useMemo(() => {
    const acc: Record<string, number> = {};
    if (!Array.isArray(rows)) return acc;
    for (const p of rows) {
      const cur = p.currency || "";
      const amt = Number(p.amount) || 0;
      acc[cur] = (acc[cur] || 0) + amt;
    }
    return acc;
  }, [rows]);

  const columns: Column<PaymentRow>[] = [
    {
      header: "Item",
      cell: (row) => (
        <div className="min-w-0">
          {row.content_type ? (
            <span className="mr-1 inline-flex rounded-full bg-brand/5 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-brand ring-1 ring-inset ring-brand/15">
              {row.content_type.replace(/-/g, " ")}
            </span>
          ) : null}
          <span className="font-medium text-gray-900">
            {row.content_name ?? "—"}
          </span>
          {row.start_date ? (
            <p className="text-[11px] text-gray-500">
              {row.start_date}
              {row.start_time ? ` · ${row.start_time}` : ""}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      header: "Amount",
      cell: (row) => (
        <span className="font-mono font-semibold tabular-nums text-gray-900">
          {row.currency ?? ""} {Number(row.amount ?? 0).toFixed(2)}
        </span>
      ),
    },
    {
      header: "Card",
      cell: (row) => (
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase text-gray-900">
            {row.card_type ?? "—"} · {row.card_last_4digits ?? "—"} ·{" "}
            {row.card_expiry_date ?? "—"}
          </p>
          <p className="truncate text-[11px] text-gray-500">{row.tx_ref ?? ""}</p>
        </div>
      ),
    },
    {
      header: "Status",
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <StatusBadge status={row.status_message} />
          {row.createdAt ? (
            <span className="text-[11px] text-gray-500">
              {new Date(row.createdAt).toDateString()}
            </span>
          ) : null}
        </div>
      ),
    },
  ];

  const totalsRow = (
    <Card padding="sm" className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold tracking-wide text-gray-700">
        Current total:
      </span>
      {Object.keys(totalsByCurrency).length > 0 ? (
        Object.entries(totalsByCurrency).map(([cur, amt]) => (
          <span
            key={cur || "default"}
            className="inline-flex items-center rounded-full bg-brand/5 px-3 py-1 text-xs font-semibold text-brand ring-1 ring-inset ring-brand/15"
          >
            <span className="font-mono tabular-nums">
              {cur || "—"} {amt.toFixed(2)}
            </span>
          </span>
        ))
      ) : (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-200">
          <span className="font-mono tabular-nums">0.00</span>
        </span>
      )}
    </Card>
  );

  return (
    <ListPage<PaymentRow>
      title="Payments"
      subtitle="Transactions and billing details."
      icon={<HiOutlineCreditCard className="h-5 w-5" />}
      toolbar={totalsRow}
      rows={rows}
      columns={columns}
      rowKey={(r) => (r as any).id ?? r.tx_ref ?? `${r.createdAt ?? ""}-${r.amount ?? ""}`}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search by name or transaction…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchPayments(p, ps);
        },
      }}
      empty={
        hasCompany
          ? "No payments yet."
          : "This account isn't attached to a company, so there are no payments to show."
      }
    />
  );
};

export default Overview;
